import { __assign, __rest, __spreadArrays } from "tslib";
import React, { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import styles from './styles.css.js';
import Dropdown from '../internal/components/dropdown';
import { useDropdownStatus } from '../internal/components/dropdown-status';
import Filter from '../select/parts/filter';
import Trigger from '../select/parts/trigger';
import SelectedTokens from '../internal/components/selected-tokens';
import { LiveRegion } from '../internal/components/live-region';
import { getBaseProps } from '../internal/base-component';
import { prepareOptions } from '../internal/components/option/utils/prepare-options';
import { useSelect } from '../select/utils/use-select';
import { useNativeSearch } from '../select/utils/use-native-search';
import { fireNonCancelableEvent } from '../internal/events';
import { useSelectedTokens } from './utils/use-selected-tokens';
import { useLoadItems } from '../select/utils/use-load-items';
import { useAnnouncement } from '../select/utils/use-announcement';
import { useFocusTracker } from '../internal/hooks/use-focus-tracker';
import { useFormFieldContext } from '../internal/context/form-field-context';
import { findOptionIndex } from '../select/utils/connect-options';
import PlainList from '../select/parts/plain-list';
import VirtualList from '../select/parts/virtual-list';
import DropdownFooter from '../internal/components/dropdown-footer/index.js';
import { checkOptionValueField } from '../select/utils/check-option-value-field.js';
import mergeRefs from 'react-merge-refs';
var InternalMultiselect = React.forwardRef(function (_a, externalRef) {
    var _b = _a.options, options = _b === void 0 ? [] : _b, _c = _a.filteringType, filteringType = _c === void 0 ? 'none' : _c, filteringPlaceholder = _a.filteringPlaceholder, filteringAriaLabel = _a.filteringAriaLabel, ariaRequired = _a.ariaRequired, placeholder = _a.placeholder, disabled = _a.disabled, ariaLabel = _a.ariaLabel, _d = _a.statusType, statusType = _d === void 0 ? 'finished' : _d, empty = _a.empty, loadingText = _a.loadingText, finishedText = _a.finishedText, errorText = _a.errorText, recoveryText = _a.recoveryText, noMatch = _a.noMatch, selectedAriaLabel = _a.selectedAriaLabel, renderHighlightedAriaLive = _a.renderHighlightedAriaLive, _e = _a.selectedOptions, selectedOptions = _e === void 0 ? [] : _e, deselectAriaLabel = _a.deselectAriaLabel, _f = _a.keepOpen, keepOpen = _f === void 0 ? true : _f, tokenLimit = _a.tokenLimit, i18nStrings = _a.i18nStrings, onBlur = _a.onBlur, onFocus = _a.onFocus, onLoadItems = _a.onLoadItems, onChange = _a.onChange, virtualScroll = _a.virtualScroll, _g = _a.hideTokens, hideTokens = _g === void 0 ? false : _g, _h = _a.__internalRootRef, __internalRootRef = _h === void 0 ? null : _h, restProps = __rest(_a, ["options", "filteringType", "filteringPlaceholder", "filteringAriaLabel", "ariaRequired", "placeholder", "disabled", "ariaLabel", "statusType", "empty", "loadingText", "finishedText", "errorText", "recoveryText", "noMatch", "selectedAriaLabel", "renderHighlightedAriaLive", "selectedOptions", "deselectAriaLabel", "keepOpen", "tokenLimit", "i18nStrings", "onBlur", "onFocus", "onLoadItems", "onChange", "virtualScroll", "hideTokens", "__internalRootRef"]);
    checkOptionValueField('Multiselect', 'options', options);
    var baseProps = getBaseProps(restProps);
    var formFieldContext = useFormFieldContext(restProps);
    var _j = useLoadItems({
        onLoadItems: onLoadItems,
        options: options,
        statusType: statusType
    }), handleLoadMore = _j.handleLoadMore, handleRecoveryClick = _j.handleRecoveryClick, fireLoadItems = _j.fireLoadItems;
    var _k = useState(''), filteringValue = _k[0], setFilteringValue = _k[1];
    var updateSelectedOption = useCallback(function (option) {
        var newSelectedOptions = __spreadArrays(selectedOptions);
        var index = findOptionIndex(newSelectedOptions, option);
        if (index > -1) {
            newSelectedOptions.splice(index, 1);
        }
        else {
            newSelectedOptions.push(option);
        }
        fireNonCancelableEvent(onChange, {
            selectedOptions: newSelectedOptions
        });
    }, [onChange, selectedOptions]);
    var _l = prepareOptions(options, filteringType, filteringValue), filteredOptions = _l.filteredOptions, parentMap = _l.parentMap;
    var rootRef = useRef(null);
    useFocusTracker({ rootRef: rootRef, onBlur: onBlur, onFocus: onFocus });
    var scrollToIndex = useRef(null);
    var _m = useSelect({
        selectedOptions: selectedOptions,
        updateSelectedOption: updateSelectedOption,
        options: filteredOptions,
        filteringType: filteringType,
        rootRef: rootRef,
        externalRef: externalRef,
        keepOpen: keepOpen,
        fireLoadItems: fireLoadItems,
        setFilteringValue: setFilteringValue
    }), isOpen = _m.isOpen, highlightedOption = _m.highlightedOption, highlightedIndex = _m.highlightedIndex, getTriggerProps = _m.getTriggerProps, getFilterProps = _m.getFilterProps, getMenuProps = _m.getMenuProps, getOptionProps = _m.getOptionProps, isKeyboard = _m.isKeyboard, highlightOption = _m.highlightOption, announceSelected = _m.announceSelected;
    var handleNativeSearch = useNativeSearch({
        isEnabled: filteringType === 'none' && isOpen,
        options: filteredOptions,
        highlightOption: highlightOption,
        highlightedOption: highlightedOption === null || highlightedOption === void 0 ? void 0 : highlightedOption.option,
        isKeyboard: isKeyboard
    });
    var isEmpty = !options || options.length === 0;
    var isNoMatch = filteredOptions && filteredOptions.length === 0;
    var dropdownStatusContent = useDropdownStatus({
        statusType: statusType,
        empty: empty,
        loadingText: loadingText,
        finishedText: finishedText,
        errorText: errorText,
        recoveryText: recoveryText,
        isEmpty: isEmpty,
        isNoMatch: isNoMatch,
        noMatch: noMatch,
        onRecoveryClick: handleRecoveryClick
    });
    var filter = (React.createElement(Filter, __assign({ filteringType: filteringType, placeholder: filteringPlaceholder, ariaLabel: filteringAriaLabel, ariaRequired: ariaRequired, value: filteringValue }, getFilterProps())));
    var trigger = (React.createElement(Trigger, __assign({ placeholder: placeholder, disabled: disabled, ariaLabel: ariaLabel, triggerProps: getTriggerProps(disabled), selectedOption: null, isOpen: isOpen }, formFieldContext)));
    var menuProps = __assign(__assign({}, getMenuProps()), { onLoadMore: handleLoadMore });
    var announcement = useAnnouncement({
        announceSelected: announceSelected,
        highlightedOption: highlightedOption,
        parentMap: parentMap,
        selectedAriaLabel: selectedAriaLabel,
        renderHighlightedAriaLive: renderHighlightedAriaLive
    });
    var selectedTokensProps = useSelectedTokens({
        disabled: disabled,
        tokenLimit: tokenLimit,
        i18nStrings: i18nStrings,
        selectedOptions: selectedOptions,
        updateSelectedOption: updateSelectedOption,
        updateFocus: function () { return getTriggerProps().ref.current.focus(); }
    });
    useEffect(function () {
        var _a;
        (_a = scrollToIndex.current) === null || _a === void 0 ? void 0 : _a.call(scrollToIndex, highlightedIndex);
    }, [highlightedIndex]);
    var ListComponent = virtualScroll ? VirtualList : PlainList;
    var handleMouseDown = function (event) {
        var target = event.target;
        if (target !== document.activeElement) {
            // prevent currently focused element from losing it
            event.preventDefault();
        }
    };
    return (React.createElement("div", __assign({}, baseProps, { ref: mergeRefs([rootRef, __internalRootRef]), className: clsx(styles.root, baseProps.className), onKeyPress: handleNativeSearch }),
        React.createElement(Dropdown, { open: isOpen, trigger: trigger, header: filter, onMouseDown: handleMouseDown, footer: React.createElement(DropdownFooter, { content: dropdownStatusContent }) },
            React.createElement(ListComponent, { menuProps: menuProps, getOptionProps: getOptionProps, filteredOptions: filteredOptions, filteringValue: filteringValue, isKeyboard: isKeyboard.current, ref: scrollToIndex, hasDropdownStatus: dropdownStatusContent !== null, checkboxes: true })),
        !hideTokens && React.createElement(SelectedTokens, __assign({}, selectedTokensProps, { deselectAriaLabel: deselectAriaLabel })),
        isKeyboard && React.createElement(LiveRegion, { message: announcement })));
});
export default InternalMultiselect;
//# sourceMappingURL=internal.js.map